import { useState, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpcClient } from "@/lib/trpc";
import {
  GitBranch,
  ArrowRight,
  Clock,
  Shield,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  getStatusLabel,
  type StatusRegistryKey,
} from "@/config/status-registry";
import { NumericInput } from "@/components/ui/numeric-input";

// ── 10 Color Palette cho workflow badges ──
const COLOR_PALETTE: {
  key: string;
  label: string;
  bg: string;
  text: string;
  border: string;
  dot: string;
}[] = [
  {
    key: "slate",
    label: "Xám",
    bg: "bg-slate-100",
    text: "text-slate-600",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
  {
    key: "amber",
    label: "Vàng",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
    dot: "bg-amber-400",
  },
  {
    key: "emerald",
    label: "Xanh lá",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-400",
  },
  {
    key: "blue",
    label: "Xanh dương",
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
    dot: "bg-blue-400",
  },
  {
    key: "teal",
    label: "Xanh ngọc",
    bg: "bg-teal-50",
    text: "text-teal-700",
    border: "border-teal-200",
    dot: "bg-teal-400",
  },
  {
    key: "indigo",
    label: "Chàm",
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-200",
    dot: "bg-indigo-400",
  },
  {
    key: "violet",
    label: "Tím",
    bg: "bg-violet-50",
    text: "text-violet-700",
    border: "border-violet-200",
    dot: "bg-violet-400",
  },
  {
    key: "rose",
    label: "Đỏ hồng",
    bg: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-200",
    dot: "bg-rose-400",
  },
  {
    key: "orange",
    label: "Cam",
    bg: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-200",
    dot: "bg-orange-400",
  },
  {
    key: "cyan",
    label: "Lam nhạt",
    bg: "bg-cyan-50",
    text: "text-cyan-700",
    border: "border-cyan-200",
    dot: "bg-cyan-400",
  },
];

function getColorByKey(key: string | null | undefined) {
  return COLOR_PALETTE.find((c) => c.key === key) || null;
}

// Auto-detect color for status (default fallback when no custom color is set)
function getAutoStatusColor(status: string) {
  const s = status?.toUpperCase() || "";
  if (s === "DRAFT" || s === "NEW" || s === "START")
    return getColorByKey("slate")!;
  if (s === "PENDING" || s === "SENT" || s === "WAITING" || s === "PROCESSING")
    return getColorByKey("amber")!;
  if (s === "CONFIRMED" || s === "APPROVED" || s === "ACCEPTED")
    return getColorByKey("emerald")!;
  if (
    s === "IN_PRODUCTION" ||
    s === "IN_PROGRESS" ||
    s === "RUNNING" ||
    s === "RELEASED" ||
    s === "PLANNED"
  )
    return getColorByKey("blue")!;
  if (s === "COMPLETED" || s === "DONE" || s === "DELIVERED" || s === "CLOSED")
    return getColorByKey("teal")!;
  if (s === "INVOICED" || s === "PAID" || s === "PARTIAL_PAID")
    return getColorByKey("violet")!;
  if (
    s === "CANCELLED" ||
    s === "REJECTED" ||
    s === "FAILED" ||
    s === "RETURNED"
  )
    return getColorByKey("rose")!;
  if (
    s === "ON_HOLD" ||
    s === "PAUSED" ||
    s === "MAINTENANCE" ||
    s === "SUSPENDED"
  )
    return getColorByKey("orange")!;
  if (s.startsWith("PARTIAL") || s === "REWORK" || s === "RE_INSPECT")
    return getColorByKey("cyan")!;
  return getColorByKey("indigo")!;
}

// Auto-detect color for action
function getAutoActionColor(action: string) {
  const a = action?.toLowerCase() || "";
  if (a.includes("tạo") || a.includes("thêm") || a.includes("mở"))
    return getColorByKey("emerald")!;
  if (a.includes("duyệt") || a.includes("xác nhận") || a.includes("chấp nhận"))
    return getColorByKey("blue")!;
  if (a.includes("gửi") || a.includes("chuyển") || a.includes("phát hành"))
    return getColorByKey("amber")!;
  if (
    a.includes("hủy") ||
    a.includes("từ chối") ||
    a.includes("xóa") ||
    a.includes("trả lại")
  )
    return getColorByKey("rose")!;
  if (
    a.includes("sản xuất") ||
    a.includes("kế hoạch") ||
    a.includes("xuất kho")
  )
    return getColorByKey("indigo")!;
  if (a.includes("giao") || a.includes("vận chuyển") || a.includes("nhận"))
    return getColorByKey("teal")!;
  if (
    a.includes("thanh toán") ||
    a.includes("hóa đơn") ||
    a.includes("thu tiền")
  )
    return getColorByKey("violet")!;
  if (a.includes("hoàn") || a.includes("đóng") || a.includes("kết thúc"))
    return getColorByKey("cyan")!;
  if (a.includes("tạm") || a.includes("dừng") || a.includes("treo"))
    return getColorByKey("orange")!;
  return getColorByKey("slate")!;
}

// Resolve: saved color > auto-detect
function resolveColor(
  savedKey: string | null | undefined,
  autoFn: () => ReturnType<typeof getColorByKey>,
) {
  return getColorByKey(savedKey) || autoFn()!;
}

// ── Process → StatusRegistryKey mapping ──
const PROCESS_STATUS_MAP: Record<string, StatusRegistryKey> = {
  order: "OrderStatus",
  quotation: "QuotationStatus",
  production: "ProductionOrderStatus",
  production_order: "ProductionOrderStatus",
  delivery: "DeliveryStatus",
  purchasing: "PurchaseOrderStatus",
  "material-planning": "MaterialPlanningStatus",
  tooling: "ToolingStatus",
  warehouse: "InventoryStatus",
};

/** Tra cứu label tiếng Việt cho status key theo process */
function getStatusLabelForProcess(process: string, statusKey: string): string {
  const registryKey = PROCESS_STATUS_MAP[process];
  if (registryKey) {
    const label = getStatusLabel(registryKey, statusKey);
    // getStatusLabel trả về raw key nếu không tìm thấy → so sánh để phân biệt
    if (label !== statusKey) return label;
  }
  // Fallback: humanize key
  return statusKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Flow order for sidebar sorting ──
const FLOW_ORDER: Record<
  string,
  { order: number; icon: string; label: string }
> = {
  quotation: { order: 1, icon: "📥", label: "Đầu vào" },
  order: { order: 2, icon: "📥", label: "Đầu vào" },
  production_order: { order: 3, icon: "⚙️", label: "Quá trình" },
  material_request: { order: 4, icon: "⚙️", label: "Quá trình" },
  production: { order: 5, icon: "⚙️", label: "Quá trình" },
  delivery: { order: 6, icon: "📤", label: "Đầu ra" },
  invoice: { order: 7, icon: "📤", label: "Đầu ra" },
  payment: { order: 8, icon: "📤", label: "Đầu ra" },
  maintenance: { order: 9, icon: "🔧", label: "Hỗ trợ" },
  warehouse: { order: 10, icon: "🔧", label: "Hỗ trợ" },
};
const FLOW_GROUPS = [
  { key: "Đầu vào", icon: "📥", processes: ["quotation", "order"] },
  {
    key: "Quá trình",
    icon: "⚙️",
    processes: ["production_order", "material_request", "production"],
  },
  { key: "Đầu ra", icon: "📤", processes: ["delivery", "invoice", "payment"] },
  { key: "Hỗ trợ", icon: "🔧", processes: ["maintenance", "warehouse"] },
];

export default function WorkflowPage() {
  const queryClient = useQueryClient();
  const [selectedProcess, setSelectedProcess] = useState("");
  const [expandedFlowGroups, setExpandedFlowGroups] = useState<Set<string>>(
    new Set(["Đầu vào", "Quá trình", "Đầu ra", "Hỗ trợ"]),
  );
  const [showDialog, setShowDialog] = useState(false);
  const [editingTransition, setEditingTransition] = useState<any>(null);
  const [formData, setFormData] = useState({
    action: "",
    fromStatus: "",
    toStatus: "",
    leadtime: "",
    responsibility: "",
  });

  // ── Fetch workflow processes ──
  const { data: wfProcessesData } = useQuery({
    queryKey: ["workflow-processes"],
    queryFn: async () => await trpcClient.config.getWorkflowProcesses.query(),
  });
  const wfProcesses = (wfProcessesData || []) as {
    process: string;
    label: string;
  }[];

  // Sort by flow order
  const sortedProcesses = useMemo(() => {
    return [...wfProcesses].sort((a, b) => {
      const oA = FLOW_ORDER[a.process]?.order ?? 99;
      const oB = FLOW_ORDER[b.process]?.order ?? 99;
      return oA - oB;
    });
  }, [wfProcesses]);

  // Auto-select first process
  if (!selectedProcess && sortedProcesses.length > 0) {
    setSelectedProcess(sortedProcesses[0].process);
  }

  // ── Fetch ALL transitions to count per process ──
  const transitionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of wfProcesses) {
      counts[p.process] = 0;
    }
    return counts;
  }, [wfProcesses]);

  // ── Fetch transitions for selected process ──
  const {
    data: transitionsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["workflow-transitions", selectedProcess],
    queryFn: async () => {
      if (!selectedProcess) return [];
      return await trpcClient.config.getTransitionsByProcess.query({
        process: selectedProcess,
      });
    },
    enabled: !!selectedProcess,
  });
  const transitions = (transitionsData || []) as any[];

  // Update counts when transitions load
  if (selectedProcess && transitions.length > 0) {
    transitionCounts[selectedProcess] = transitions.length;
  }

  // ── Derive unique statuses from transitions for dropdown ──
  const uniqueStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const t of transitions) {
      if (t.fromStatus) set.add(t.fromStatus);
      if (t.toStatus) set.add(t.toStatus);
    }
    return Array.from(set).sort();
  }, [transitions]);

  const statusOptions = useMemo(() => {
    return uniqueStatuses.map((s) => ({
      id: s,
      label: `${s} - ${getStatusLabelForProcess(selectedProcess, s)}`,
    }));
  }, [uniqueStatuses, selectedProcess]);

  // ── Fetch counts for all processes ──
  const { data: allTransData } = useQuery({
    queryKey: ["workflow-all-transitions"],
    queryFn: async () => {
      const results: Record<string, number> = {};
      for (const p of wfProcesses) {
        try {
          const trans = await trpcClient.config.getTransitionsByProcess.query({
            process: p.process,
          });
          results[p.process] = (trans as any[]).length;
        } catch {
          results[p.process] = 0;
        }
      }
      return results;
    },
    enabled: wfProcesses.length > 0,
  });
  const allCounts = allTransData || {};

  // ── CRUD mutations ──
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await trpcClient.config.deleteWorkflowTransition.mutate({ id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["workflow-transitions"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["workflow-all-transitions"],
      });
    },
    onError: () => toast.error("Xóa thất bại"),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingTransition?.id) {
        return await trpcClient.config.updateWorkflowTransition.mutate({
          id: editingTransition.id,
          data,
        });
      }
      return await trpcClient.config.addWorkflowTransition.mutate({
        ...data,
        process: selectedProcess,
      });
    },
    onSuccess: async () => {
      setShowDialog(false);
      setEditingTransition(null);
      await queryClient.invalidateQueries({
        queryKey: ["workflow-transitions"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["workflow-all-transitions"],
      });
    },
    onError: () => toast.error("Lưu thất bại"),
  });

  const openAdd = () => {
    setEditingTransition(null);
    setFormData({
      action: "",
      fromStatus: "",
      toStatus: "",
      leadtime: "",
      responsibility: "",
    });
    setShowDialog(true);
  };

  const openEdit = (t: any) => {
    setEditingTransition(t);
    setFormData({
      action: t.action || "",
      fromStatus: t.fromStatus || "",
      toStatus: t.toStatus || "",
      leadtime: String(t.leadtime || ""),
      responsibility: t.responsibility || "",
    });
    setShowDialog(true);
  };

  const handleSave = () => {
    saveMutation.mutate({
      action: formData.action,
      fromStatus: formData.fromStatus,
      toStatus: formData.toStatus,
      leadtime: formData.leadtime ? Number(formData.leadtime) : null,
      responsibility: formData.responsibility || null,
    });
  };

  const toggleFlowGroup = (key: string) => {
    setExpandedFlowGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="h-[calc(100vh-64px)] flex">
      {/* ═══ SIDEBAR ═══ */}
      <div className="w-[220px] border-r border-slate-200 bg-slate-50/50 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-indigo-600" /> Quy trình WF
          </h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Workflow Transitions
          </p>
        </div>
        <ScrollArea className="flex-1">
          {FLOW_GROUPS.map((fg) => {
            const isExpanded = expandedFlowGroups.has(fg.key);
            const groupProcesses = sortedProcesses.filter((p) =>
              fg.processes.includes(p.process),
            );
            const groupTotal = groupProcesses.reduce(
              (s, p) => s + ((allCounts as any)[p.process] || 0),
              0,
            );

            return (
              <div key={fg.key}>
                <button
                  className={cn(
                    "w-full px-3 py-2 text-left flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors",
                    isExpanded
                      ? "bg-slate-100 text-slate-700"
                      : "text-slate-400 hover:bg-slate-50",
                  )}
                  onClick={() => toggleFlowGroup(fg.key)}
                >
                  <ChevronDown
                    className={cn(
                      "w-3 h-3 transition-transform",
                      !isExpanded && "-rotate-90",
                    )}
                  />
                  <span>
                    {fg.icon} {fg.key}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-auto text-[9px] px-1 py-0 bg-slate-200 font-bold"
                  >
                    {groupTotal}
                  </Badge>
                </button>
                {isExpanded &&
                  groupProcesses.map((p) => {
                    const isActive = selectedProcess === p.process;
                    const count = (allCounts as any)[p.process] || 0;
                    return (
                      <button
                        key={p.process}
                        className={cn(
                          "w-full text-left pl-8 pr-3 py-2 text-xs flex items-center justify-between transition-all",
                          isActive
                            ? "bg-indigo-50 text-indigo-700 font-semibold"
                            : "text-slate-600 hover:bg-slate-100",
                        )}
                        onClick={() => setSelectedProcess(p.process)}
                      >
                        <span className="truncate">{p.label}</span>
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 bg-slate-100 shrink-0 ml-1"
                        >
                          {count}
                        </Badge>
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </ScrollArea>
        <div className="p-3 border-t border-slate-200 text-[10px] text-slate-400 text-center">
          {wfProcesses.length} quy trình
        </div>
      </div>

      {/* ═══ CONTENT ═══ */}
      <div className="flex-1 flex flex-col min-w-0">
        {selectedProcess && (
          <div className="px-5 py-3 border-b border-slate-100 bg-white flex items-center gap-3">
            <GitBranch className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">
              {wfProcesses.find((p) => p.process === selectedProcess)?.label ||
                selectedProcess}
            </h3>
            <Badge variant="secondary" className="text-[10px] h-5 px-2">
              {transitions.length} bước
            </Badge>
            <div className="ml-auto">
              <Button size="sm" className="h-7 text-xs" onClick={openAdd}>
                <Plus className="w-3 h-3 mr-1" /> Thêm bước
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {selectedProcess ? (
            <Table>
              <TableHeader className="apv-table-header sticky top-0 z-10">
                <TableRow className="border-b-0 hover:bg-transparent">
                  <TableHead className="w-[50px] py-3">#</TableHead>
                  <TableHead>Hành động</TableHead>
                  <TableHead>Từ trạng thái</TableHead>
                  <TableHead className="w-[30px]"></TableHead>
                  <TableHead>Sang trạng thái</TableHead>
                  <TableHead>Leadtime</TableHead>
                  <TableHead>Trách nhiệm</TableHead>
                  <TableHead className="w-[80px] text-right">
                    Thao tác
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-slate-400 text-sm"
                    >
                      Đang tải...
                    </TableCell>
                  </TableRow>
                )}
                {transitions.map((t, idx) => (
                  <TableRow
                    key={t.id}
                    className="hover:bg-slate-50/50 transition-colors h-[44px]"
                  >
                    <TableCell className="text-xs text-slate-400 font-mono">
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const ac = resolveColor(t.actionColor, () =>
                          getAutoActionColor(t.action),
                        );
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className={cn(
                                  "font-medium text-sm px-2 py-0.5 rounded-md inline-flex items-center gap-1.5 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-200 transition-all",
                                  ac.bg,
                                  ac.text,
                                )}
                              >
                                <span
                                  className={cn(
                                    "w-2 h-2 rounded-full shrink-0",
                                    ac.dot,
                                  )}
                                />
                                {t.action}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-[200px] p-2"
                              align="start"
                            >
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 px-1">
                                Chọn màu hành động
                              </p>
                              <div className="grid grid-cols-5 gap-1">
                                {COLOR_PALETTE.map((cp) => (
                                  <button
                                    key={cp.key}
                                    title={cp.label}
                                    className={cn(
                                      "h-7 rounded-md flex items-center justify-center transition-all hover:scale-110 border",
                                      cp.bg,
                                      cp.border,
                                      t.actionColor === cp.key &&
                                        "ring-2 ring-offset-1 ring-blue-400",
                                    )}
                                    onClick={() => {
                                      trpcClient.config.updateWorkflowTransition
                                        .mutate({
                                          id: t.id,
                                          data: { actionColor: cp.key },
                                        })
                                        .then(() => {
                                          refetch();
                                        });
                                    }}
                                  >
                                    <span
                                      className={cn(
                                        "w-3 h-3 rounded-full",
                                        cp.dot,
                                      )}
                                    />
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                      {t.lockEdit && (
                        <Badge
                          variant="secondary"
                          className="ml-2 text-[9px] h-4 bg-amber-100 text-amber-700"
                        >
                          <Shield className="w-2.5 h-2.5 mr-0.5" />
                          Khóa
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const c = resolveColor(t.fromColor, () =>
                          getAutoStatusColor(t.fromStatus),
                        );
                        const fromLabel = getStatusLabelForProcess(
                          selectedProcess,
                          t.fromStatus,
                        );
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className={cn(
                                  "text-[10px] font-mono px-2 py-0.5 rounded-md border inline-flex items-center gap-1 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-200 transition-all",
                                  c.bg,
                                  c.text,
                                  c.border,
                                )}
                              >
                                <span
                                  className={cn(
                                    "w-1.5 h-1.5 rounded-full shrink-0",
                                    c.dot,
                                  )}
                                />
                                <span className="flex flex-col items-start leading-tight">
                                  <span>{t.fromStatus}</span>
                                  <span className="text-[9px] opacity-70 font-sans">
                                    {fromLabel}
                                  </span>
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-[200px] p-2"
                              align="start"
                            >
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 px-1">
                                Màu trạng thái nguồn
                              </p>
                              <div className="grid grid-cols-5 gap-1">
                                {COLOR_PALETTE.map((cp) => (
                                  <button
                                    key={cp.key}
                                    title={cp.label}
                                    className={cn(
                                      "h-7 rounded-md flex items-center justify-center transition-all hover:scale-110 border",
                                      cp.bg,
                                      cp.border,
                                      t.fromColor === cp.key &&
                                        "ring-2 ring-offset-1 ring-blue-400",
                                    )}
                                    onClick={() => {
                                      trpcClient.config.updateWorkflowTransition
                                        .mutate({
                                          id: t.id,
                                          data: { fromColor: cp.key },
                                        })
                                        .then(() => {
                                          refetch();
                                        });
                                    }}
                                  >
                                    <span
                                      className={cn(
                                        "w-3 h-3 rounded-full",
                                        cp.dot,
                                      )}
                                    />
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const c = resolveColor(t.toColor, () =>
                          getAutoStatusColor(t.toStatus),
                        );
                        const toLabel = getStatusLabelForProcess(
                          selectedProcess,
                          t.toStatus,
                        );
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className={cn(
                                  "text-[10px] font-mono px-2 py-0.5 rounded-md border inline-flex items-center gap-1 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-200 transition-all",
                                  c.bg,
                                  c.text,
                                  c.border,
                                )}
                              >
                                <span
                                  className={cn(
                                    "w-1.5 h-1.5 rounded-full shrink-0",
                                    c.dot,
                                  )}
                                />
                                <span className="flex flex-col items-start leading-tight">
                                  <span>{t.toStatus}</span>
                                  <span className="text-[9px] opacity-70 font-sans">
                                    {toLabel}
                                  </span>
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-[200px] p-2"
                              align="start"
                            >
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 px-1">
                                Màu trạng thái đích
                              </p>
                              <div className="grid grid-cols-5 gap-1">
                                {COLOR_PALETTE.map((cp) => (
                                  <button
                                    key={cp.key}
                                    title={cp.label}
                                    className={cn(
                                      "h-7 rounded-md flex items-center justify-center transition-all hover:scale-110 border",
                                      cp.bg,
                                      cp.border,
                                      t.toColor === cp.key &&
                                        "ring-2 ring-offset-1 ring-blue-400",
                                    )}
                                    onClick={() => {
                                      trpcClient.config.updateWorkflowTransition
                                        .mutate({
                                          id: t.id,
                                          data: { toColor: cp.key },
                                        })
                                        .then(() => {
                                          refetch();
                                        });
                                    }}
                                  >
                                    <span
                                      className={cn(
                                        "w-3 h-3 rounded-full",
                                        cp.dot,
                                      )}
                                    />
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {t.leadtime ? (
                        <span className="text-xs text-slate-600 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />{" "}
                          {t.leadtime}d
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {t.responsibility ? (
                        <span className="text-xs text-slate-600">
                          {t.responsibility}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(t);
                          }}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-500 hover:text-red-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Xóa transition?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Bạn có chắc muốn xóa bước "{t.action}"?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Hủy</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(t.id)}
                              >
                                Xóa
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && transitions.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-12 text-slate-400"
                    >
                      <GitBranch className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">
                        Chưa có transitions cho quy trình này
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 h-full">
              <div className="text-center">
                <GitBranch className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Chọn quy trình từ sidebar</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ ADD/EDIT DIALOG ═══ */}
      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowDialog(false);
            setEditingTransition(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingTransition?.id ? "Sửa Transition" : "Thêm Transition"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium text-slate-700">
                Hành động
              </label>
              <Input
                value={formData.action}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, action: e.target.value }))
                }
                placeholder="VD: Duyệt báo giá"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">
                  Từ trạng thái
                </label>
                <ComboboxFK
                  value={formData.fromStatus}
                  onChange={(v) =>
                    setFormData((p) => ({ ...p, fromStatus: v }))
                  }
                  options={statusOptions}
                  placeholder="Chọn trạng thái"
                  canAdd={false}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">
                  Sang trạng thái
                </label>
                <ComboboxFK
                  value={formData.toStatus}
                  onChange={(v) => setFormData((p) => ({ ...p, toStatus: v }))}
                  options={statusOptions}
                  placeholder="Chọn trạng thái"
                  canAdd={false}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">
                  Leadtime (ngày)
                </label>
                <NumericInput
                  value={formData.leadtime}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, leadtime: e.target.value }))
                  }
                  placeholder="VD: 3"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">
                  Trách nhiệm
                </label>
                <Input
                  value={formData.responsibility}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      responsibility: e.target.value,
                    }))
                  }
                  placeholder="VD: Sale Admin"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Hủy
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                !formData.action || !formData.fromStatus || !formData.toStatus
              }
            >
              {editingTransition?.id ? "Cập nhật" : "Thêm mới"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
